// Langfuse connector: connect a project's Langfuse keys, see a test case's Langfuse trace, and turn production
// traces into tests. Keys are write-only (stored encrypted); only the host, project name and a key prefix come back.
import React, { useState } from 'react';
import { api } from './engApi';
import { useMe, MANAGE_HINT } from './me';
import { Err } from './ui';

const REGIONS = [['eu', 'EU (cloud.langfuse.com)'], ['us', 'US (us.cloud.langfuse.com)'], ['jp', 'Japan'], ['custom', 'Self-hosted URL']];

export function LangfuseCard({ proj, reload }) {
  const me = useMe();
  const info = proj.integrations?.langfuse;
  const [f, setF] = useState({ region: 'eu', url: '', public_key: '', secret_key: '' });
  const [open, setOpen] = useState(false); const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);
  async function connect() {
    setBusy(true); setErr(null);
    try {
      await api.connectLangfuse(proj.project_id, { host: f.region === 'custom' ? f.url.trim() : f.region, public_key: f.public_key, secret_key: f.secret_key });
      setOpen(false); setF({ ...f, public_key: '', secret_key: '' }); await reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }
  async function disconnect() { setBusy(true); try { await api.disconnectLangfuse(proj.project_id); await reload(); } catch (e) { setErr(e); } finally { setBusy(false); } }
  return (
    <div className="ef-card"><div className="hd">Langfuse <span className="x xs fnt">optional</span></div><div className="bd sm">
      <Err e={err} />
      {info && !open ? <>
        <div>✓ Connected to Langfuse project <b>{info.project_name}</b> <span className="fnt">({info.url}, key {info.public_key})</span></div>
        <div className="xs fnt" style={{ margin: '6px 0 8px' }}>The debugger can load each test case's Langfuse trace, and <b>Test cases → Import from Langfuse</b> turns production traces into tests.</div>
        {me.can_manage && <div style={{ display: 'flex', gap: 8 }}><button className="ef-btn sm ghost" onClick={() => setOpen(true)}>Replace keys</button>
          <button className="ef-btn sm red" disabled={busy} onClick={disconnect}>Disconnect</button></div>}</>
      : !open ? <>
        <div className="mut">Already trace your bot in Langfuse? Connect it to see each test's LLM, tool and retrieval steps in the debugger, and to turn real conversations into tests.</div>
        <button className="ef-btn sm" style={{ marginTop: 8 }} disabled={!me.can_manage} title={me.can_manage ? '' : MANAGE_HINT} onClick={() => setOpen(true)}>Connect Langfuse</button></>
      : <div className="ef-grid" style={{ gap: 8 }}>
        <div className="ef-field" style={{ margin: 0 }}><label>Region</label>
          <select className="ef-inp" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })}>{REGIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        {f.region === 'custom' && <input className="ef-inp mono" placeholder="https://langfuse.your-company.com" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />}
        <input className="ef-inp mono" placeholder="Public key: pk-lf-…" value={f.public_key} onChange={(e) => setF({ ...f, public_key: e.target.value })} />
        <input className="ef-inp mono" type="password" autoComplete="off" placeholder="Secret key: sk-lf-…" value={f.secret_key} onChange={(e) => setF({ ...f, secret_key: e.target.value })} />
        <div className="xs fnt">From Langfuse → project settings → API keys. NinjaEval only reads traces; the keys are encrypted and never shown again.</div>
        <div style={{ display: 'flex', gap: 8 }}><button className="ef-btn sm" disabled={busy || !f.public_key.trim() || !f.secret_key.trim()} onClick={connect}>{busy ? 'Checking…' : 'Connect'}</button>
          <button className="ef-btn sm ghost" onClick={() => setOpen(false)}>Cancel</button></div>
      </div>}
    </div></div>
  );
}

// One version's Langfuse trace for a test case, loaded on demand.
const DOCS = '/docs/developer-guide.html#connect-langfuse';
const day = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

// Why there's no trace, in words a tester can act on.
export function missing(d) {
  switch (d.reason) {
    case 'no_trace_id': return <>This run is from before NinjaEval sent trace ids, so Langfuse can't match it. Runs from now on will show here.</>;
    case 'before_connect': return <>This run happened before Langfuse was connected{d.connected_at ? ` (${day(d.connected_at)})` : ''}, so it has no trace. New runs will show here.</>;
    case 'arriving': return <>Waiting for Langfuse: traces arrive a few seconds after the run. Checking again…</>;
    default: return <>Langfuse has no trace for this run. Your service needs to continue the trace NinjaEval starts (the <span className="mono">traceparent</span> header). <a href={DOCS} target="_blank" rel="noreferrer">How to set it up</a></>;
  }
}

// One run's trace: loads by itself, and re-checks while a fresh trace is still arriving.
export function useLangfuse(execId) {
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  React.useEffect(() => {
    let stop = false, tries = 0, timer;
    async function load() {
      try {
        const x = await api.langfuseTrace(execId);
        if (stop) return;
        setD(x); setErr(null);
        if (!x.found && x.reason === 'arriving' && ++tries < 8) timer = setTimeout(load, 15000);
      } catch (e) { if (!stop) setErr(e); }
    }
    setD(null); setErr(null);
    if (execId) load();
    return () => { stop = true; clearTimeout(timer); };
  }, [execId]);
  return { d, err };
}

// Recent production traces → draft test cases. "Went wrong" lists only traces where a step errored or that got a
// low score or negative feedback in Langfuse: the production failures worth keeping as regression tests.
export function ImportFromLangfuse({ proj, onPick, onClose }) {
  const [only, setOnly] = useState('flagged');
  const [list, setList] = useState(null); const [err, setErr] = useState(null); const [busy, setBusy] = useState('');
  React.useEffect(() => {
    setList(null); setErr(null);
    api.langfuseTraces(proj.project_id, only).then((r) => setList(r.traces)).catch(setErr);
  }, [proj.project_id, only]);
  async function pick(t) {
    setBusy(t.trace_id);
    try {
      const c = await api.langfuseCase(proj.project_id, t.trace_id);
      onPick({ title: c.title, query: c.query, followups: c.followups || [], expected_outcome: '',
        // a failed trace's tool calls are what went wrong, not what should happen: don't expect them
        expected_calls: t.flag ? null : c.expected_calls, tags: t.flag ? ['production', t.flag.kind === 'error' ? 'prod-error' : 'prod-feedback'] : ['production'] });
    } catch (e) { setErr(e); } finally { setBusy(''); }
  }
  return (
    <div className="ef-card" style={{ marginBottom: 12 }}><div className="hd">Import from Langfuse <span className="x">
      <span style={{ display: 'inline-flex', gap: 4, marginRight: 10 }}>{[['flagged', '⚠ Went wrong'], ['', 'All recent']].map(([k, l]) =>
        <button key={k || 'all'} type="button" className={'ef-btn sm ' + (only === k ? '' : 'ghost')} onClick={() => setOnly(k)}>{l}</button>)}</span>
      <button type="button" className="ef-link sm" onClick={onClose}>Close</button></span></div><div className="bd">
      <Err e={err} />
      {only === 'flagged' && <div className="xs fnt" style={{ marginBottom: 8 }}>Traces from the last 7 days where a step errored, or that got a low score or negative
        feedback (a 👎 recorded as a Langfuse score of 0 or false, or a numeric score ≤ 0.5). Turning them into tests stops the same failure coming back.</div>}
      {!list && !err && <div className="sm fnt">Loading the last 7 days of traces…</div>}
      {list && !list.length && <div className="sm fnt">{only === 'flagged' ? 'Nothing went wrong in the last 7 days: no errors and no low scores.' : 'No traces in the last 7 days.'}</div>}
      {list && list.length > 0 && <table className="t"><thead><tr><th>Trace</th>{only === 'flagged' && <th>Why it's flagged</th>}<th>User said</th><th>Bot replied</th><th /></tr></thead><tbody>
        {list.map((t) => <tr key={t.trace_id}><td><b>{t.name || 'trace'}</b><div className="xs fnt">{(t.time || '').replace('T', ' ').slice(0, 16)}</div></td>
          {only === 'flagged' && <td className="sm" style={{ maxWidth: 220, wordBreak: 'break-word' }}><span className={'ef-st ' + (t.flag?.kind === 'error' ? 'reg' : 'w')}>{t.flag?.kind === 'error' ? 'Error' : 'Low score'}</span>
            <div className="xs mut">{t.flag?.detail}</div></td>}
          <td className="sm" style={{ maxWidth: 260, wordBreak: 'break-word' }}>{t.user_message || <span className="fnt">{t.input}</span>}</td>
          <td className="sm mut" style={{ maxWidth: 260, wordBreak: 'break-word' }}>{t.output}</td>
          <td><button className="ef-btn sm" disabled={!!busy} onClick={() => pick(t)}>{busy === t.trace_id ? '…' : 'Use as test'}</button></td></tr>)}</tbody></table>}
      <div className="xs fnt" style={{ marginTop: 6 }}>The test gets everything the user said (every message of a conversation)
        {only === 'flagged' ? ', tagged production. You add what a good answer does, so the next change is checked against it.' : ' and the tool calls that trace made. You add what a good answer does.'}</div>
    </div></div>
  );
}
